import React, { FC } from 'react';
import { DeliveryCard, Loader } from '@fdha/web-ui-library';
import { Box, Typography } from '@mui/material';
import {
  AutomationItemType,
  DeliveryV2,
  MenuType,
  useGetProfileQuery,
} from '@fdha/graphql-api-admin';
import { useNavigate } from 'react-router';
import { isDeliveryDelayed } from '@fdha/common-utils';

import {
  useAutomation,
  useDeliveryDialog,
  useGetDeliveriesWithSync,
} from '../../../../../hooks';
import { AutomationDetails } from '../../../../../components';

interface ActiveDeliveriesProps {
  patientId?: string;
  subjectId?: string | null;
}

const ActiveDeliveries: FC<ActiveDeliveriesProps> = ({
  patientId = '',
  subjectId,
}) => {
  const navigate = useNavigate();
  const { data: profileData } = useGetProfileQuery();
  const userId = profileData?.me.id;

  const { deliveries, isLoading } = useGetDeliveriesWithSync(patientId);

  const { openReactivateDialog, openCancelDialog } = useDeliveryDialog(
    subjectId ?? ''
  );

  const { automationItems } = useAutomation(patientId);

  const renderEmptyState = () => (
    <Box display="flex" justifyContent="center">
      <Typography variant="body2">No upcoming deliveries.</Typography>
    </Box>
  );

  const LabelHelper = ({ menuType }: { menuType: MenuType }) => {
    if (menuType === MenuType.Regular) {
      return;
    }

    const items = automationItems.filter(
      (item) => item.type === AutomationItemType.FrozenFood
    );

    return <AutomationDetails automationItems={items} />;
  };

  return (
    <Box>
      {isLoading ? (
        <Loader />
      ) : !deliveries?.length ? (
        renderEmptyState()
      ) : (
        deliveries?.map((delivery: DeliveryV2) => (
          <DeliveryCard
            key={delivery.id}
            updatedAt={delivery.updatedAt}
            updatedById={delivery.updatedById}
            updatedByName={delivery.updatedByName}
            currentUserId={userId}
            deliveryId={delivery.id}
            deliveryDate={delivery.deliveryDate}
            openChanges={delivery.openChanges}
            status={delivery.status}
            shipmentLabels={delivery.order?.shipmentLabels}
            showShipmentStatus
            isCanceled={delivery.isCanceled}
            isOwner={false}
            showIconMenu
            onClick={() =>
              navigate(`/patient/${patientId}/delivery/${delivery.id}`)
            }
            onEdit={() =>
              navigate(`/patient/${patientId}/delivery/${delivery.id}`, {
                state: { editMode: true },
              })
            }
            onCancel={openCancelDialog}
            onReactivate={openReactivateDialog}
            buttonLabel="See more details"
            weekOfCycle={delivery.weekOfCycle}
            isDelayed={isDeliveryDelayed(
              new Date(delivery.deliveryDate),
              delivery.order?.shipmentLabels
            )}
            menuType={delivery.dietPlanDeliveryMenu.type}
            labelHelper={
              <LabelHelper menuType={delivery.dietPlanDeliveryMenu.type} />
            }
            daysCovered={delivery.daysCovered ?? 0}
            weekdaysCovered={delivery.weekdaysCovered ?? []}
          />
        ))
      )}
    </Box>
  );
};

export default ActiveDeliveries;
